/**
 * Lesson completion gating.
 *
 * Lessons complete themselves once the student has actually gone through
 * them — there is no "mark complete" button to click past the content.
 *
 * Two requirements, both must be met for a reading lesson:
 *   1. Scrolled to the end of the lesson body
 *   2. Spent at least a realistic minimum reading time for its length
 *
 * Interactive workshops use their own signal instead: reaching the final
 * screen of the player means every step was stepped through.
 */

// Teens read ~200 wpm; require they spend at least the time it would take
// to read at 3x that speed — fast skimming still counts, instant does not.
const ASSUMED_SKIM_WPM = 600;
const MIN_SECONDS = 15;
const MAX_SECONDS = 240; // never hold anyone hostage on a very long lesson

/** Strip markdown/HTML and count words. */
export function countWords(content) {
  if (!content || typeof content !== 'string') return 0;
  const text = content
    .replace(/<[^>]+>/g, ' ')
    .replace(/[#>*_`|\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.split(' ').length : 0;
}

/**
 * Minimum seconds a student must spend before a lesson can auto-complete.
 * Mirrors requiredSecondsFor() in backend/src/routes/courses.js — keep the
 * two in sync or the server will reject completions the UI allows.
 */
export function requiredSeconds(content) {
  // Workshops are gated by finishing their steps, not by reading time; time
  // them by step count so the server's check matches.
  const text = String(content || '').trim();
  if (text.startsWith('{') && (text.includes('"workshop"') || text.includes('"airlearn"'))) {
    try {
      const steps = JSON.parse(text).steps;
      if (Array.isArray(steps)) {
        return Math.min(180, Math.max(MIN_SECONDS, steps.length * 4));
      }
    } catch (_) {
      // fall through
    }
  }

  const words = countWords(content);
  const seconds = Math.round((words / ASSUMED_SKIM_WPM) * 60);
  return Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, seconds));
}

/**
 * Has the element (or the page) been scrolled to the bottom of the content?
 * @param {HTMLElement|null} el - the lesson body element
 * @param {number} slack - px of tolerance
 */
export function isScrolledToEnd(el, slack = 120) {
  if (!el) return false;

  // The lesson body scrolls with the page in this app, so measure the
  // element's bottom against the viewport.
  const rect = el.getBoundingClientRect();
  const viewportBottom = window.innerHeight || document.documentElement.clientHeight;

  // Content shorter than the viewport is fully visible already
  if (rect.height <= viewportBottom) return true;

  return rect.bottom - slack <= viewportBottom;
}
